import React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import { EmtTypography } from "./EmtTypography";
import { EmtIcon } from "./EmtIcon";
import { EmtGridContainer } from "./EmtGridContainer";
import { EmtTheme } from "../types";

export type EmtFooterProps = {
  footerTop?: React.ReactNode;
  footerBottom?: {
    /**
     * Text before copyright
     * @default undefined
     */
    text?: React.ReactNode | string;
    copyright: React.ReactNode | string;
  };
  logoLinks: React.ReactNode;
  firstLinks?: React.ReactNode;
  secondLinks?: React.ReactNode;
  thirdLinks?: React.ReactNode;
  nonce?: string;
};

const Footer = styled("footer")(({ theme }) => ({
  display: "flex",
  overflow: "hidden",
  alignItems: "center",
  justifyContent: "center",
  background: theme.palette.primary.main,
  minHeight: "200px",
  padding: "38px 0px",
  borderTopLeftRadius: "18px",
  borderTopRightRadius: "18px",
  position: "relative",
  zIndex: 3,
  [theme.breakpoints.down("md")]: {
    paddingBottom: "120px",
  },
}));

const GridLogo = styled(Grid)(({ theme }) => ({
  "& img, & svg": {
    width: "100%",
    height: "auto",
    overflow: "hidden",
  },
  "& img": {
    maxWidth: "105px",
    [theme.breakpoints.up("md")]: {},
  },
  "& img.logo": {
    maxWidth: "200px",
    [theme.breakpoints.up("md")]: {
      maxWidth: "220px",
    },
  },
}));

const FooterDivider = styled(Divider)(({ theme }: { theme?: EmtTheme }) => ({
  margin: "auto",
  borderColor: `${theme?.palette.primary[100]}`,
}));
const StyledEmtTypography = styled(EmtTypography)(
  ({ theme }: { theme?: EmtTheme }) => ({
    color: theme?.palette.primary[100],
  })
);

const SquareEmtIcon = styled(EmtIcon)(({ theme }: { theme?: EmtTheme }) => ({
  color: theme?.palette.primary[100],
  border: `1px solid ${theme?.palette.primary[100]}`,
  borderRadius: "8px",
  padding: "5px",
  paddingTop: "5.2px",
  fontSize: "24px",
  position: "relative",
}));

export function EmtFooter(props: EmtFooterProps) {
  const {
    footerTop,
    footerBottom,
    logoLinks,
    firstLinks,
    secondLinks,
    thirdLinks,
  } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <>
      {footerTop}
      <Footer>
        <Box className="EmtReactNativeWebViewPaddingBottom">
          <Container>
            <EmtGridContainer
              columnSpacing={{ xs: 4, lg: 5 }}
              rowSpacing={{ xs: 4, lg: 2 }}
              justifyContent="flex-end"
            >
              {/* Logo + mobile sotre */}
              <GridLogo item xs={12} md={6} lg={3} sx={{ marginRight: "auto" }}>
                <>{logoLinks}</>
              </GridLogo>

              {isMobile && (
                <Grid item xs={12}>
                  <FooterDivider />
                </Grid>
              )}

              {/* firstLinks */}
              {firstLinks && (
                <Grid item xs={12} md={6} lg={3}>
                  <>{firstLinks}</>
                </Grid>
              )}

              {isMobile && secondLinks && (
                <Grid item xs={12}>
                  <FooterDivider />
                </Grid>
              )}

              {/* secondLinks */}
              {secondLinks && (
                <Grid item xs={12} md={6} lg={3}>
                  <>{secondLinks}</>
                </Grid>
              )}

              {thirdLinks && isMobile && (
                <Grid item xs={12}>
                  <FooterDivider />
                </Grid>
              )}

              {/* thirdlinks */}
              {thirdLinks && (
                <Grid item xs={12} md={6} lg={3}>
                  <>{thirdLinks}</>
                </Grid>
              )}

              {/* Bottom Footer */}
              {footerBottom && (
                <>
                  <Grid item xs={12} sx={{ marginTop: "10px" }}>
                    <FooterDivider
                      sx={{
                        width: "100%",
                      }}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sx={{
                      textAlign: isMobile ? "left" : "right",
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      justifyContent: isMobile ? "center" : "end",
                    }}
                    justifyContent={isMobile ? "start" : "end"}
                  >
                    <StyledEmtTypography variant="caption">
                      {footerBottom.text && (
                        <>
                          {footerBottom.text}
                          <br />
                        </>
                      )}
                      <>{footerBottom.copyright}</>
                    </StyledEmtTypography>
                    <SquareEmtIcon
                      className="emt-arrow-up"
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
                      }
                    />
                  </Grid>
                </>
              )}
            </EmtGridContainer>
          </Container>
        </Box>
      </Footer>
    </>
  );
}
